import React, { useEffect, useRef } from 'react';
import {
  ACCOUNT_READY_MESSAGE,
  BETA_ALLOWANCE_UNAVAILABLE_MESSAGE,
  BETA_ALLOWANCE_USED_MESSAGE,
  BETA_BLOCKED_MESSAGE,
  OWN_KEY_IN_USE_MESSAGE,
  allowanceBudget,
  allowanceLabel,
  allowanceSwitchCopy,
  betaWriterLabel,
  isBetaWriter,
  usedOfLimitLabel,
} from '../../utils/betaAllowance';
import { openSettingsAtApiKey } from '../../utils/openSettings';
import './FuelGaugeDetailModal.css';

export function FuelGaugeDetailModal({ data, onClose, onRefresh, isRefreshCooldown, onOpenSettings }) {
  const modalRef = useRef(null);

  // Escape key dismisses modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus trap — focus modal on mount
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  const handleAddCredits = () => {
    window.open('https://openrouter.ai/credits', '_blank', 'noopener');
  };

  const handleGoToSettings = () => {
    onClose();
    if (onOpenSettings) onOpenSettings();
  };

  const handleAddOwnKey = () => {
    onClose();
    if (onOpenSettings) onOpenSettings('api-key');
    else openSettingsAtApiKey();
  };

  if (data?.key_provisioning === 'pending') {
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>Beta allowance</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <p>{betaWriterLabel(data.allowance_label, ACCOUNT_READY_MESSAGE)}</p>
          </div>
          <div className="fg-modal__actions">
            <button className="fg-btn fg-btn--secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (data?.key_provisioning === 'blocked') {
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>Beta allowance</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <p>{betaWriterLabel(data.allowance_label, BETA_BLOCKED_MESSAGE)}</p>
          </div>
          <div className="fg-modal__actions">
            <button className="fg-btn fg-btn--secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (data?.key_provisioning === 'own_needed') {
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>Beta allowance</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <p>{BETA_ALLOWANCE_USED_MESSAGE}</p>
          </div>
          <div className="fg-modal__actions">
            <button type="button" className="fg-btn fg-btn--primary" onClick={handleAddOwnKey}>
              Add your own key
            </button>
            <button type="button" className="fg-btn fg-btn--secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (
    isBetaWriter(data)
    && data.key_provisioning !== 'own'
    && data.key_provisioning !== 'own_needed'
    && typeof data.balance === 'number'
    && Number.isFinite(data.balance)
  ) {
    const balance = data.balance;
    const budget = allowanceBudget({
      balance,
      limit: data.limit,
      usage: data.usage,
    });
    const usagePercent = budget.limitCents
      ? Math.min((budget.usedCents / budget.limitCents) * 100, 100)
      : 0;
    const usedLine = usedOfLimitLabel(budget);
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>Beta allowance</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <div className="fg-balance-panel">
              <h3 className="fg-balance-panel__amount">
                <span className="fg-mono">{allowanceLabel(balance)}</span>
              </h3>
              {usedLine && (
                <p className="fg-balance-panel__detail">
                  {usedLine}
                </p>
              )}
              <div className="fg-progress" role="progressbar" aria-valuenow={usagePercent} aria-valuemin="0" aria-valuemax="100">
                <div className="fg-progress__fill" style={{ width: `${usagePercent}%` }} />
              </div>
            </div>
            <AllowanceSwitchNote balance={balance} onAddOwnKey={handleAddOwnKey} />
          </div>
          <div className="fg-modal__actions">
            <RefreshButton onRefresh={onRefresh} isRefreshCooldown={isRefreshCooldown} />
          </div>
        </div>
      </div>
    );
  }

  // A beta read that failed, or a beta key reported invalid, stays on the
  // allowance. Nothing below this is an own-key panel.
  if (isBetaWriter(data) && data.key_provisioning !== 'own' && data.key_provisioning !== 'own_needed') {
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>Beta allowance</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <p>{BETA_ALLOWANCE_UNAVAILABLE_MESSAGE}</p>
          </div>
          <div className="fg-modal__actions">
            <RefreshButton onRefresh={onRefresh} isRefreshCooldown={isRefreshCooldown} />
            <button className="fg-btn fg-btn--secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // --- No key state ---
  if (!data || data.error === 'No OpenRouter key configured') {
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>Connect OpenRouter</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <p>Connect your OpenRouter API key to power AI brainstorming. OpenRouter provides access to multiple AI models at competitive rates.</p>
          </div>
          <div className="fg-modal__actions">
            <button className="fg-btn fg-btn--primary" onClick={handleGoToSettings}>
              Go to Settings
            </button>
            <button className="fg-btn fg-btn--secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Invalid key state ---
  if (data.error && data.error.includes('Invalid')) {
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>Invalid API Key</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <p>Your OpenRouter API key appears to be invalid or revoked. Please update it in Settings.</p>
          </div>
          <div className="fg-modal__actions">
            <button className="fg-btn fg-btn--primary" onClick={handleGoToSettings}>
              Update Key in Settings
            </button>
            <button className="fg-btn fg-btn--secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Unlimited account ---
  if (data.is_unlimited) {
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>Pay-as-you-go Account</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <div className="fg-balance-panel fg-balance-panel--unlimited">
              <UnlimitedIconLarge />
              <p className="fg-balance-panel__usage">
                Total usage: <span className="fg-mono">${data.usage.toFixed(2)}</span>
              </p>
            </div>
            <p className="fg-info">
              Your OpenRouter account is pay-as-you-go. You'll be billed for usage at the end of each billing period.
            </p>
          </div>
          <div className="fg-modal__actions">
            <button className="fg-btn fg-btn--primary" onClick={handleAddCredits}>
              Manage Account at OpenRouter &rarr;
            </button>
            <RefreshButton onRefresh={onRefresh} isRefreshCooldown={isRefreshCooldown} />
          </div>
        </div>
      </div>
    );
  }

  // Own key, and this read has no dollar figure. Not a beta allowance.
  const ownBalance = typeof data.balance === 'number' && Number.isFinite(data.balance);
  if (data.key_provisioning === 'own' && !ownBalance) {
    return (
      <div className="fg-modal-overlay" onClick={onClose}>
        <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="fg-modal__header">
            <h2>OpenRouter key</h2>
            <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="fg-modal__body">
            <p>{OWN_KEY_IN_USE_MESSAGE}</p>
          </div>
          <div className="fg-modal__actions">
            <RefreshButton onRefresh={onRefresh} isRefreshCooldown={isRefreshCooldown} />
            <button className="fg-btn fg-btn--secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // --- Normal account with balance ---
  const balance = data.balance ?? 0;
  const usage = data.usage ?? 0;
  const limit = data.limit ?? 0;
  const usagePercent = limit > 0 ? Math.min((usage / limit) * 100, 100) : 0;

  return (
    <div className="fg-modal-overlay" onClick={onClose}>
      <div className="fg-modal" ref={modalRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="fg-modal__header">
          <h2>OpenRouter Balance</h2>
          <button className="fg-modal__close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="fg-modal__body">
          <div className="fg-balance-panel">
            <h3 className="fg-balance-panel__amount">
              <span className="fg-mono">${balance.toFixed(2)}</span> remaining
            </h3>
            <p className="fg-balance-panel__detail">
              Used: ${usage.toFixed(2)} of ${limit.toFixed(2)}
            </p>
            <div className="fg-progress" role="progressbar" aria-valuenow={usagePercent} aria-valuemin="0" aria-valuemax="100">
              <div
                className="fg-progress__fill"
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          </div>
        </div>
        <div className="fg-modal__actions">
          <button className="fg-btn fg-btn--primary" onClick={handleAddCredits}>
            Add Credits at OpenRouter &rarr;
          </button>
          <RefreshButton onRefresh={onRefresh} isRefreshCooldown={isRefreshCooldown} />
        </div>
        <div className="fg-modal__footer-info">
          <p>Credits are managed through your OpenRouter account.</p>
          <p>After adding credits, click Refresh to update.</p>
        </div>
      </div>
    </div>
  );
}

// --- Shared sub-components ---

function AllowanceSwitchNote({ balance, onAddOwnKey }) {
  const copy = allowanceSwitchCopy(balance);
  return (
    <div className="fg-info">
      <p>{copy.message}</p>
      {copy.offerKey && (
        <button type="button" className="fg-btn fg-btn--primary" onClick={onAddOwnKey}>
          Add your own key
        </button>
      )}
    </div>
  );
}

function RefreshButton({ onRefresh, isRefreshCooldown }) {
  return (
    <button
      className="fg-btn fg-btn--secondary"
      onClick={onRefresh}
      disabled={isRefreshCooldown}
      title={isRefreshCooldown ? 'Please wait before refreshing again' : 'Refresh balance'}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginRight: '6px' }} aria-hidden="true">
        <path
          d="M12 7a5 5 0 11-1.5-3.5M12 2v3h-3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Refresh
    </button>
  );
}

function UnlimitedIconLarge() {
  return (
    <svg width="48" height="28" viewBox="0 0 48 28" aria-hidden="true" style={{ marginBottom: '12px' }}>
      <path
        d="M12 4C7 4 4 9 4 14s3 10 8 10 7-5 10-10c3-5 5-10 10-10s8 5 8 10-3 10-8 10-7-5-10-10C19 9 17 4 12 4z"
        fill="none"
        stroke="var(--color-teal, #4ecdc4)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
